import { buildType } from "@helpers/app-identity";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTemplate } from "@hooks/useAppTemplate";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { usePremiumStudyPackDist } from "@hooks/usePremiumStudyPackDist";
import { Apps } from "@memorang/constants/apps";
import type { Section as HomeSection } from "@memorang/ui/components/HomeApplet/types";
import { getFreeSampleBannerCopy } from "../../helpers/freemium-home";
import type { RecommendedSkillTask } from "../../recommendations/adapters/dashboard-task-types";
import { useItemTypesSection } from "../use-item-types-section";
import { useFeaturesSection } from "../useFeaturesSection";
import { useHasAccess } from "../useHasAccess";
import useItemsCount from "../useItemsCount";
import { useMasteryKeyTopicsSectionData } from "../useMasteryKeyTopicsSectionData";
import { useRecommendedTasksSection } from "../useRecommendedTasksSection";
import { generatePracticeTestSubtitle } from "./helpers";

type Params = {
	onSelectTags?: (tagIds: string[]) => void;
	onStartRecommendedSession?: (task: RecommendedSkillTask) => void;
	onShowPurchase?: () => void;
};

export const useHomeSections = ({
	onSelectTags,
	onStartRecommendedSession,
	onShowPurchase,
}: Params = {}) => {
	const { hasStudyPackContentAccess } = useHasAccess();
	const { data: studyPackData, isLoading: isLoadingStudyPackData } =
		useMixedItemDist({
			action: "GET_STUDYPACK_DISTRIBUTION",
		});
	// APP-1623's free-sample upsell (banner + locked premium section) is JBJS-only for now —
	// other partners can share this exam data shape (e.g. TOEIC Link, PTE Core) without their
	// home screen being ready for it. See usePremiumStudyPackDist's doc comment.
	const { data: premiumStudyPackData } = usePremiumStudyPackDist({
		enabled: buildType === Apps.Jbjs,
	});

	const { isFetchingItemsCount, numPracticeTests, numQuiz } = useItemsCount();

	const diagnosticLabel = useAppStore((state) => state.exam.diagnosticLabel);
	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);

	const tasksSectionData = useRecommendedTasksSection({
		studyPackData,
		isLoadingStudyPackData,
		onSelectTags,
		onStartRecommendedSession,
	});

	const productsSectionData = useFeaturesSection({
		isFetchingItemsCount,
		practiceTestSubtitle: generatePracticeTestSubtitle({
			numPracticeTests,
			numQuiz,
			practiceTestLabel,
			diagnosticLabel,
		}),
		numPracticeTests,
	});

	const topicsSectionData = useMasteryKeyTopicsSectionData(
		studyPackData,
		hasStudyPackContentAccess,
		premiumStudyPackData,
	);

	const { isLanguageLearningApp } = useAppTemplate();
	const itemTypesSectionData = useItemTypesSection();

	const visibleTopicsSectionData = (topicsSectionData ?? []).filter(
		(s) => s.isEnabled || s.isLoading,
	);
	// Only present the free/premium split once there's a locked premium section to contrast
	// against, and a handler to open the paywall from the chip/banner.
	const hasPremiumSection =
		visibleTopicsSectionData.some((s) => s.isPremium) &&
		Boolean(onShowPurchase);
	const firstFreeSectionIndex = visibleTopicsSectionData.findIndex(
		(s) => !s.isPremium,
	);
	const firstFreeSection = visibleTopicsSectionData[firstFreeSectionIndex];
	const freeSampleBannerCopy = hasPremiumSection
		? getFreeSampleBannerCopy({
				freeDist: studyPackData,
				premiumDist: premiumStudyPackData,
				freeTopicsGroup: firstFreeSection
					? { type: firstFreeSection.type, tags: firstFreeSection.data }
					: undefined,
			})
		: undefined;

	const topicsSections: HomeSection[] = visibleTopicsSectionData.map(
		(s, index) => ({
			isLoading: s.isLoading,
			header: {
				title: s.sectionTitle,
				...(hasPremiumSection
					? {
							premiumChip: s.isPremium
								? { variant: "premium" as const, onPress: onShowPurchase }
								: { variant: "free" as const },
						}
					: {}),
			},
			type: "topics" as const,
			data: [{ items: s.data }],
			onPress: s.onPress,
			...(freeSampleBannerCopy &&
			onShowPurchase &&
			index === firstFreeSectionIndex
				? { banner: { ...freeSampleBannerCopy, onPress: onShowPurchase } }
				: {}),
		}),
	);

	const sections: HomeSection[] = [
		...(tasksSectionData?.isEnabled
			? [
					{
						isLoading: tasksSectionData.isLoading,
						header: {
							title: tasksSectionData.sectionTitle,
						},
						type: "tasks" as const,
						data: [{ items: tasksSectionData.data }],
					},
				]
			: []),
		...(productsSectionData.isEnabled
			? [
					{
						isLoading: productsSectionData.isLoading,
						header: {
							title: productsSectionData.sectionTitle,
						},
						type: "products" as const,
						data: [{ items: productsSectionData.data }],
					},
				]
			: []),
		...(isLanguageLearningApp
			? itemTypesSectionData.isEnabled || itemTypesSectionData.isLoading
				? [
						{
							isLoading: itemTypesSectionData.isLoading,
							header: {
								title: itemTypesSectionData.sectionTitle,
							},
							type: "itemTypes" as const,
							data: [{ items: itemTypesSectionData.data }],
						},
					]
				: []
			: topicsSections),
	];

	return {
		sections,
	};
};
